import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

/*
The Review tab of the script editor sidebar: one card per element of the same
`script_elements` array the d3 diagram draws, summarizing the properties the
diagram can only hint at — attribs, connector targets, routing weights, and how
many elements point back. Cards reuse the `side-menu-list-obj` shell so they
match the rest of the sidebar.
*/

export interface ScriptConnector {
  key?: string;
  ordinal_position?: number;
  value_text?: string | null;
  next_el?: number | null;
  next_el_hash?: string | null;
  next_el_display_name?: string | null;
  response_count?: number | null;
  rules?: { weight?: number }[] | null;
}

export interface ScriptElement {
  element_id?: number;
  element_hash: string;
  display_name?: string | null;
  prompt?: string | null;
  elementtype?: string | null;
  iconClass?: string | null;
  pos_top?: number | string | null;
  pos_left?: number | string | null;
  response_count?: number | null;
  attribs?: Record<string, unknown> | null;
  connectors?: ScriptConnector[] | null;
}

/** One entry of `ScriptEdit`'s `elementTypes`, used here for labels only. */
export interface ScriptElementTypeConfig {
  id: number;
  name: string;
  label?: string;
  iconClass?: string;
}

export type ScriptElementSortKey =
  | 'script_order'
  | 'display_name'
  | 'prompt'
  | 'elementtype';

export interface ScriptElementListProps {
  data: ScriptElement[];
  elementTypes?: ScriptElementTypeConfig[];
  editingElement?: ScriptElement | null;
  /** Omit to render read-only — cards stop being clickable. */
  onEditElementRequest?: (element: ScriptElement) => void;
  initialSortKey?: ScriptElementSortKey;
}

const SORT_OPTIONS: { value: ScriptElementSortKey; label: string }[] = [
  { value: 'script_order', label: 'Script order' },
  { value: 'display_name', label: 'Display name' },
  { value: 'prompt', label: 'Prompt' },
  { value: 'elementtype', label: 'Element type' },
];

function humanizeType(name?: string | null): string {
  if (!name) return 'Unknown';
  return name.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatAttribValue(value: unknown): string {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Randomizer / voter-routed weights live on the connector's rules array. */
function connectorWeight(conn: ScriptConnector): number | undefined {
  if (!Array.isArray(conn.rules)) return undefined;
  return conn.rules.find((r) => typeof r?.weight !== 'undefined')?.weight;
}

function elementLabel(element: ScriptElement): string {
  return element.display_name || '(unnamed)';
}

/** Chips here sit inside a 30vw sidebar, so they run smaller than MUI's small. */
function MetaChip({
  label,
  color,
  title,
}: {
  label: string;
  color?: 'default' | 'warning';
  title?: string;
}) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={label}
      title={title}
      sx={{
        height: 18,
        fontSize: 10,
        maxWidth: '100%',
        '& .MuiChip-label': { px: 0.6 },
      }}
    />
  );
}

export default function ScriptElementList({
  data,
  elementTypes = [],
  editingElement = null,
  onEditElementRequest,
  initialSortKey = 'script_order',
}: ScriptElementListProps) {
  const [sortKey, setSortKey] = useState<ScriptElementSortKey>(initialSortKey);
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');

  const elements = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const typeLabels = useMemo(() => {
    const map = new Map<string, string>();
    elementTypes.forEach((t) => map.set(t.name, t.label || humanizeType(t.name)));
    return map;
  }, [elementTypes]);

  const typeLabelFor = useCallback(
    (element: ScriptElement) =>
      typeLabels.get(element.elementtype || '') || humanizeType(element.elementtype),
    [typeLabels]
  );

  const namesByHash = useMemo(() => {
    const map = new Map<string, string>();
    elements.forEach((el) => map.set(el.element_hash, elementLabel(el)));
    return map;
  }, [elements]);

  const inboundByHash = useMemo(() => {
    const map = new Map<string, string[]>();
    elements.forEach((el) => {
      (el.connectors || []).forEach((conn) => {
        if (!conn.next_el_hash) return;
        const sources = map.get(conn.next_el_hash) || [];
        const name = elementLabel(el);
        if (!sources.includes(name)) sources.push(name);
        map.set(conn.next_el_hash, sources);
      });
    });
    return map;
  }, [elements]);

  const sorted = useMemo(() => {
    const indexed = elements.map((element, index) => ({ element, index }));
    const dir = direction === 'asc' ? 1 : -1;

    if (sortKey === 'script_order') {
      return dir === 1 ? indexed : indexed.reverse();
    }

    const sortText = ({ element }: { element: ScriptElement }) => {
      if (sortKey === 'display_name') return (element.display_name || '').trim();
      if (sortKey === 'prompt') return (element.prompt || '').trim();
      return typeLabelFor(element);
    };

    return indexed.sort((a, b) => {
      const av = sortText(a);
      const bv = sortText(b);

      // Elements with nothing to sort on sink to the bottom either way; a run of
      // blank prompts at the top tells you nothing.
      if (!av !== !bv) return av ? -1 : 1;

      const compared =
        av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * dir;

      // Ties hold script order, so grouping by type still reads like the script.
      return compared || a.index - b.index;
    });
  }, [elements, sortKey, direction, typeLabelFor]);

  if (!elements.length) {
    return (
      <Box className="side-menu-list-obj">
        <Typography variant="caption" color="text.secondary">
          This revision has no script elements yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          py: 0.75,
          mb: 0.5,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <TextField
          select
          size="small"
          label="Sort by"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as ScriptElementSortKey)}
          sx={{ flexGrow: 1 }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip title={direction === 'asc' ? 'Sort descending' : 'Sort ascending'}>
          <IconButton
            size="small"
            aria-label={direction === 'asc' ? 'Sort descending' : 'Sort ascending'}
            onClick={() => setDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
          >
            <Box
              component="i"
              className={`fas fa-arrow-${direction === 'asc' ? 'down' : 'up'}`}
              aria-hidden
              sx={{ fontSize: 12 }}
            />
          </IconButton>
        </Tooltip>

        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {elements.length} element{elements.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {sorted.map(({ element }) => {
        const isEditing =
          !!editingElement?.element_hash &&
          editingElement.element_hash === element.element_hash;
        const isStart = element.elementtype === 'start';
        const canEdit = !!onEditElementRequest && !isStart;

        const connectors = element.connectors || [];
        const attribs =
          element.attribs && typeof element.attribs === 'object' ? element.attribs : {};
        const attribKeys = Object.keys(attribs);
        const inbound = inboundByHash.get(element.element_hash) || [];

        return (
          <Box
            key={element.element_hash}
            className={`side-menu-list-obj${isEditing ? ' focused' : ''}`}
            onClick={canEdit ? () => onEditElementRequest(element) : undefined}
            sx={{ cursor: canEdit ? 'pointer' : 'default' }}
          >
            <div className="list-obj-inner-wrapper">
              <Stack direction="row" alignItems="flex-start" spacing={0.75} className="obj-title">
                <Box
                  component="i"
                  className={element.iconClass || 'fas fa-circle'}
                  aria-hidden
                  sx={{ mt: '2px', width: 14, textAlign: 'center', flexShrink: 0 }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0, lineHeight: 1.3 }}>
                  {elementLabel(element)}
                </Box>
                <MetaChip label={typeLabelFor(element)} title={element.elementtype || undefined} />
              </Stack>

              <Stack spacing={0.5} className="obj-data">
                {!!element.prompt && (
                  <Typography
                    variant="caption"
                    title={element.prompt}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.35,
                    }}
                  >
                    {element.prompt}
                  </Typography>
                )}

                {!!attribKeys.length && (
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {attribKeys.map((key) => (
                      <MetaChip key={key} label={`${key}: ${formatAttribValue(attribs[key])}`} />
                    ))}
                  </Stack>
                )}

                {!!connectors.length && (
                  <Stack spacing={0.25}>
                    {connectors.map((conn, i) => {
                      const weight = connectorWeight(conn);
                      const targetName = conn.next_el_hash
                        ? namesByHash.get(conn.next_el_hash)
                        : undefined;
                      const targetLabel = !conn.next_el_hash
                        ? 'not connected'
                        : targetName || conn.next_el_display_name || 'missing element';

                      return (
                        <Typography
                          key={conn.key || `${element.element_hash}-${conn.ordinal_position ?? i}`}
                          variant="caption"
                          sx={{ lineHeight: 1.35 }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: conn.value_text ? 'inherit' : 'text.secondary' }}
                          >
                            {conn.value_text || 'pass through'}
                          </Box>
                          {typeof weight === 'number' && (
                            <Box component="span" sx={{ color: 'text.secondary' }}> ({weight}%)</Box>
                          )}
                          <Box component="span" sx={{ color: 'text.secondary' }}> &rarr; </Box>
                          <Box
                            component="span"
                            sx={{ color: targetName ? 'text.secondary' : 'warning.main' }}
                          >
                            {targetLabel}
                          </Box>
                        </Typography>
                      );
                    })}
                  </Stack>
                )}

                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {isStart ? null : inbound.length ? (
                    <MetaChip
                      label={`${inbound.length} inbound`}
                      title={inbound.join(', ')}
                    />
                  ) : (
                    <MetaChip label="no inbound" color="warning" />
                  )}
                  {typeof element.response_count === 'number' && element.response_count > 0 && (
                    <MetaChip label={`${element.response_count} responses`} />
                  )}
                </Stack>
              </Stack>
            </div>
          </Box>
        );
      })}
    </Box>
  );
}
